/**
 * Smoke Drive shares — internal (Admin / Production) and external (public download page).
 * Copied links are always wholesale.londonsmokeandcure.co.uk/files/… never Storage URLs.
 */

import {
    collection,
    doc,
    getDoc,
    getDocs,
    increment,
    query,
    serverTimestamp,
    setDoc,
    Timestamp,
    updateDoc,
    where,
} from 'firebase/firestore';
import { db } from './firebase';
import { userHasAdminOrProductionRole } from './adminDesktopNotificationService';
import {
    DRIVE_ITEM_TYPE,
    OPERATOR_DRIVE_ITEMS_COLL,
} from './operatorDriveService';
import { getOperatorDriveDownloadUrl, setOperatorDriveAttachmentDisposition } from './operatorDriveFileService';
import {
    OPERATOR_DRIVE_SHARES_COLL,
    DRIVE_SHARE_KIND,
    buildDriveShareUrl,
    createDriveShareToken,
    isDriveShareKind,
    isValidDriveShareToken,
} from '../utils/operatorDriveShareLinks';
import {
    evaluateDriveShareAccess,
    hashDriveSharePassword,
    redactDriveShareForGate,
    shareHasAccessGates,
    shareOptionsHaveGates,
    verifyDriveSharePassword,
} from '../utils/operatorDriveShareGates';
import {
    DRIVE_FOLDER_SHARE_FILE_CAP,
    DRIVE_ZIP_MAX_BYTES,
    listDriveDescendantFiles,
} from '../utils/operatorDriveLibrary';
import { driveDownloadFileName } from '../utils/operatorDriveFileName';

function assertEligible(userData) {
    if (!userHasAdminOrProductionRole(userData || {})) {
        throw new Error('Only Admin and Production can create Smoke Drive shares.');
    }
}

function toDate(value) {
    if (!value) return null;
    if (value instanceof Date && Number.isFinite(value.getTime())) return value;
    if (typeof value.toDate === 'function') {
        try {
            return value.toDate();
        } catch {
            return null;
        }
    }
    const parsed = new Date(value);
    return Number.isFinite(parsed.getTime()) ? parsed : null;
}

function mapShare(snap) {
    const data = snap.data() || {};
    return {
        id: snap.id,
        ...data,
        kind: data.kind === DRIVE_SHARE_KIND.INTERNAL
            ? DRIVE_SHARE_KIND.INTERNAL
            : DRIVE_SHARE_KIND.EXTERNAL,
        fileName: data.fileName || 'file',
        revoked: Boolean(data.revoked),
        downloadCount: Number(data.downloadCount) || 0,
        maxDownloads: Number(data.maxDownloads) || 0,
        passwordProtected: Boolean(data.passwordProtected),
        itemType: data.itemType === DRIVE_ITEM_TYPE.FOLDER
            ? DRIVE_ITEM_TYPE.FOLDER
            : DRIVE_ITEM_TYPE.FILE,
        memberUids: Array.isArray(data.memberUids) ? data.memberUids.filter(Boolean) : [],
        memberNames: Array.isArray(data.memberNames) ? data.memberNames : [],
        files: Array.isArray(data.files) ? data.files : [],
    };
}

async function resolveFileDownloadUrl(item) {
    if (item.downloadUrl) return item.downloadUrl;
    if (!item.storagePath) return '';
    return getOperatorDriveDownloadUrl(item.storagePath);
}

async function snapshotFolderFiles(item, library) {
    const files = listDriveDescendantFiles(library || [], item.id);
    if (!files.length) {
        throw new Error('This folder is empty. Add files before sharing it.');
    }
    if (files.length > DRIVE_FOLDER_SHARE_FILE_CAP) {
        throw new Error(
            `Folder shares can include at most ${DRIVE_FOLDER_SHARE_FILE_CAP} files. Share a smaller folder, or share files one by one.`
        );
    }
    const total = files.reduce((sum, file) => sum + (Number(file.sizeBytes) || 0), 0);
    if (total > DRIVE_ZIP_MAX_BYTES) {
        throw new Error(
            'This folder is larger than the 400 MB in-browser zip cap. Share files separately.'
        );
    }
    const snapshots = [];
    for (const file of files) {
        const url = await resolveFileDownloadUrl(file);
        if (!url) continue;
        snapshots.push({
            itemId: file.id,
            fileName: driveDownloadFileName(file),
            originalFileName: driveDownloadFileName({
                name: file.originalFileName || file.name,
                originalFileName: file.originalFileName,
                contentType: file.contentType,
            }),
            contentType: file.contentType || '',
            sizeBytes: Number(file.sizeBytes) || 0,
            downloadUrl: url,
            storagePath: file.storagePath || '',
        });
    }
    if (!snapshots.length) {
        throw new Error('None of the files in this folder have a download path yet.');
    }
    return snapshots;
}

async function markItemsExternalShare(itemIds, active) {
    const ids = [...new Set((itemIds || []).filter(Boolean))];
    for (const id of ids) {
        try {
            await updateDoc(doc(db, OPERATOR_DRIVE_ITEMS_COLL, id), {
                hasActiveExternalShare: Boolean(active),
                updatedAt: serverTimestamp(),
            });
        } catch {
            /* item may have been deleted */
        }
    }
}

/**
 * @param {string} kind
 * @param {string} token
 * @returns {string}
 */
export function getDriveShareWebsiteUrl(kind, token) {
    const origin = typeof window !== 'undefined' ? window.location.origin : '';
    return buildDriveShareUrl(kind, token, origin);
}

/**
 * @param {string} itemId
 * @returns {Promise<object[]>}
 */
export async function listDriveSharesForItem(itemId) {
    if (!itemId) return [];
    const q = query(
        collection(db, OPERATOR_DRIVE_SHARES_COLL),
        where('itemId', '==', itemId)
    );
    const snap = await getDocs(q);
    return snap.docs
        .map(mapShare)
        .sort((a, b) => (Number(b.createdAt?.seconds) || 0) - (Number(a.createdAt?.seconds) || 0));
}

/**
 * Create or reuse an active share, then return the website URL.
 * Extra options (password, expiry, cap, named staff) always create a new share.
 */
export async function createOrReuseDriveShare({
    itemId,
    kind,
    uid,
    userData,
    library = [],
    password = '',
    expiresAt = null,
    maxDownloads = 0,
    memberUids = [],
    memberNames = [],
}) {
    assertEligible(userData);
    if (!itemId || !uid) throw new Error('itemId and uid are required.');
    if (!isDriveShareKind(kind)) throw new Error('Share kind must be internal or external.');

    const itemRef = doc(db, OPERATOR_DRIVE_ITEMS_COLL, itemId);
    const itemSnap = await getDoc(itemRef);
    if (!itemSnap.exists()) throw new Error('Item not found.');
    const item = { id: itemSnap.id, ...(itemSnap.data() || {}) };
    if (item.trashed) throw new Error('Restore the item from Trash before sharing.');

    const options = { password, expiresAt, maxDownloads, memberUids };
    if (kind === DRIVE_SHARE_KIND.EXTERNAL && Array.isArray(memberUids) && memberUids.length) {
        throw new Error('Named people are only for Internal staff shares.');
    }

    let existingList = [];
    try {
        existingList = await listDriveSharesForItem(itemId);
    } catch {
        existingList = [];
    }

    const wantFolder = item.type === DRIVE_ITEM_TYPE.FOLDER;
    if (!shareOptionsHaveGates(options)) {
        const existing = existingList.find((share) => (
            share.kind === kind
            && !share.revoked
            && !shareHasAccessGates(share)
            && (share.itemType === DRIVE_ITEM_TYPE.FOLDER) === wantFolder
        ));
        if (existing) {
            if (item.storagePath) {
                await setOperatorDriveAttachmentDisposition(
                    item.storagePath,
                    driveDownloadFileName(item),
                    item.contentType
                );
            }
            if (kind === DRIVE_SHARE_KIND.EXTERNAL && !item.hasActiveExternalShare) {
                await markItemsExternalShare(
                    wantFolder
                        ? [itemId, ...((existing.files || []).map((f) => f.itemId))]
                        : [itemId],
                    true
                );
            }
            return {
                share: existing,
                url: getDriveShareWebsiteUrl(kind, existing.id),
                reused: true,
            };
        }
    }

    let fileSnapshots = [];
    let storageUrl = '';
    if (wantFolder) {
        fileSnapshots = await snapshotFolderFiles(item, library);
    } else if (item.type !== DRIVE_ITEM_TYPE.FILE) {
        throw new Error('Only files and folders can be shared.');
    } else {
        storageUrl = await resolveFileDownloadUrl(item);
        if (!storageUrl) throw new Error('File has no storage path.');
        if (!item.downloadUrl && item.storagePath) {
            await updateDoc(itemRef, { downloadUrl: storageUrl, updatedAt: serverTimestamp() });
        }
        if (item.storagePath) {
            await setOperatorDriveAttachmentDisposition(
                item.storagePath,
                driveDownloadFileName(item),
                item.contentType
            );
        }
        fileSnapshots = [{
            itemId: item.id,
            fileName: driveDownloadFileName(item),
            originalFileName: driveDownloadFileName({
                name: item.originalFileName || item.name,
                originalFileName: item.originalFileName,
                contentType: item.contentType,
            }),
            contentType: item.contentType || '',
            sizeBytes: Number(item.sizeBytes) || 0,
            downloadUrl: storageUrl,
            storagePath: item.storagePath || '',
        }];
    }

    const token = createDriveShareToken();
    const shareRef = doc(db, OPERATOR_DRIVE_SHARES_COLL, token);
    const expiryDate = toDate(expiresAt);
    const cap = Math.max(0, Math.floor(Number(maxDownloads) || 0));
    const members = kind === DRIVE_SHARE_KIND.INTERNAL
        ? (Array.isArray(memberUids) ? memberUids.filter(Boolean).slice(0, 80) : [])
        : [];
    const names = kind === DRIVE_SHARE_KIND.INTERNAL
        ? (Array.isArray(memberNames) ? memberNames.filter(Boolean).slice(0, 80) : [])
        : [];

    const payload = {
        kind,
        itemId,
        itemType: wantFolder ? DRIVE_ITEM_TYPE.FOLDER : DRIVE_ITEM_TYPE.FILE,
        fileName: driveDownloadFileName(item),
        originalFileName: driveDownloadFileName({
            name: item.originalFileName || item.name,
            originalFileName: item.originalFileName,
            contentType: item.contentType,
        }),
        contentType: wantFolder ? 'application/zip' : (item.contentType || ''),
        sizeBytes: wantFolder
            ? fileSnapshots.reduce((sum, file) => sum + (Number(file.sizeBytes) || 0), 0)
            : (Number(item.sizeBytes) || 0),
        storagePath: wantFolder ? '' : (item.storagePath || ''),
        downloadUrl: wantFolder ? '' : storageUrl,
        files: wantFolder ? fileSnapshots : [],
        createdByUid: uid,
        createdByName: String(userData?.displayName || userData?.fullName || userData?.email || '').slice(0, 120),
        createdAt: serverTimestamp(),
        updatedAt: serverTimestamp(),
        revoked: false,
        downloadCount: 0,
        maxDownloads: cap,
        expiresAt: expiryDate ? Timestamp.fromDate(expiryDate) : null,
        passwordProtected: false,
        passwordSalt: '',
        passwordHash: '',
        memberUids: members,
        memberNames: names,
    };

    const secret = String(password || '').trim();
    if (secret) {
        if (kind !== DRIVE_SHARE_KIND.EXTERNAL) {
            throw new Error('Password is only for External download pages.');
        }
        const hashed = await hashDriveSharePassword(secret);
        payload.passwordProtected = true;
        payload.passwordSalt = hashed.salt;
        payload.passwordHash = hashed.hash;
    }

    await setDoc(shareRef, payload);
    if (kind === DRIVE_SHARE_KIND.EXTERNAL) {
        const markIds = wantFolder
            ? [itemId, ...fileSnapshots.map((file) => file.itemId)]
            : [itemId];
        await markItemsExternalShare(markIds, true);
    }
    return {
        share: { id: token, ...payload, revoked: false },
        url: getDriveShareWebsiteUrl(kind, token),
        reused: false,
    };
}

/**
 * @param {{ shareId: string, uid: string, userData: object }} args
 */
export async function revokeDriveShare({ shareId, uid, userData }) {
    assertEligible(userData);
    if (!shareId || !uid) throw new Error('shareId and uid are required.');
    if (!isValidDriveShareToken(shareId)) throw new Error('Share token is not valid.');
    const shareRef = doc(db, OPERATOR_DRIVE_SHARES_COLL, shareId);
    const snap = await getDoc(shareRef);
    if (!snap.exists()) return;
    const data = snap.data() || {};
    const itemId = data.itemId;
    const wasExternal = data.kind === DRIVE_SHARE_KIND.EXTERNAL;
    await updateDoc(shareRef, {
        revoked: true,
        revokedAt: serverTimestamp(),
        revokedByUid: uid,
        updatedAt: serverTimestamp(),
    });
    if (wasExternal && itemId) {
        let list = [];
        try {
            list = await listDriveSharesForItem(itemId);
        } catch {
            list = [];
        }
        const stillOpen = list.some(
            (s) => s.kind === DRIVE_SHARE_KIND.EXTERNAL && !s.revoked
        );
        const fileIds = Array.isArray(data.files)
            ? data.files.map((file) => file.itemId)
            : [];
        await markItemsExternalShare([itemId, ...fileIds], stillOpen);
    }
}

function applyShareGate(share, expectedKind, uid) {
    if (share.kind !== expectedKind) return { status: 'missing' };
    if (!share.downloadUrl && !(share.files || []).length) return { status: 'missing' };
    const gate = evaluateDriveShareAccess(share, { nowMs: Date.now(), uid });
    if (gate.status === 'ok') return { status: 'ok', share };
    if (gate.status === 'need_password') {
        return { status: 'need_password', share: redactDriveShareForGate(share) };
    }
    return { status: gate.status };
}

/**
 * Public or staff read of a share token for the download page.
 */
export async function loadDriveShareForDownload(token, expectedKind, opts = {}) {
    const id = String(token || '').trim();
    if (!isValidDriveShareToken(id) || !isDriveShareKind(expectedKind)) {
        return { status: 'missing' };
    }
    try {
        const snap = await getDoc(doc(db, OPERATOR_DRIVE_SHARES_COLL, id));
        if (!snap.exists()) return { status: 'missing' };
        const share = mapShare(snap);
        if (share.revoked) return { status: 'revoked' };
        return applyShareGate(share, expectedKind, opts.uid);
    } catch {
        return { status: 'missing' };
    }
}

/**
 * Check password on the branded page, then return the full share for download.
 */
export async function unlockDriveShareWithPassword(token, expectedKind, password, opts = {}) {
    const id = String(token || '').trim();
    if (!isValidDriveShareToken(id) || !isDriveShareKind(expectedKind)) {
        return { status: 'missing' };
    }
    try {
        const snap = await getDoc(doc(db, OPERATOR_DRIVE_SHARES_COLL, id));
        if (!snap.exists()) return { status: 'missing' };
        const share = mapShare(snap);
        if (share.revoked) return { status: 'revoked' };
        if (share.kind !== expectedKind) return { status: 'missing' };
        const ok = await verifyDriveSharePassword(share, password);
        if (!ok) return { status: 'bad_password' };
        const unlocked = { ...share, passwordProtected: false };
        return applyShareGate(unlocked, expectedKind, opts.uid);
    } catch {
        return { status: 'missing' };
    }
}

/**
 * Best-effort download counter. Safe to ignore if Console rules are not applied yet.
 */
export async function recordDriveShareDownload(shareId) {
    const id = String(shareId || '').trim();
    if (!isValidDriveShareToken(id)) return;
    try {
        await updateDoc(doc(db, OPERATOR_DRIVE_SHARES_COLL, id), {
            downloadCount: increment(1),
            lastDownloadedAt: serverTimestamp(),
        });
    } catch {
        /* rules may not allow public increment yet */
    }
}

export { DRIVE_SHARE_KIND, OPERATOR_DRIVE_SHARES_COLL };
